import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { CheckIcon, MinusIcon } from 'lucide-react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

export const checkboxVariants = cva(
    'focus-visible:ring-ring/50 outline-hidden shadow-xs peer size-4 shrink-0 overflow-hidden border ring-offset-background transition-shadow focus-visible:border-ring focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
    {
        variants: {
            color: {
                default:
                    'border-gray-900 data-[state=checked]:bg-gray-900 data-[state=indeterminate]:bg-gray-900 data-[state=checked]:text-white data-[state=indeterminate]:text-white',
                brick: 'border-brick-600 data-[state=checked]:bg-brick-500 data-[state=indeterminate]:bg-brick-500 data-[state=checked]:text-white data-[state=indeterminate]:text-white',
            },
            shape: {
                default: 'rounded-[4px]',
                round: 'rounded-full',
            },
            groupHover: {
                default: '',
                light: 'group-hover:border-white group-hover:data-[state=checked]:bg-white group-hover:data-[state=checked]:text-black',
            },
            backgroundColor: {
                default: '',
                gray: 'bg-gray-200',
            },
        },
        defaultVariants: {
            color: 'default',
            shape: 'default',
            groupHover: 'default',
            backgroundColor: 'default',
        },
    },
);

export const Checkbox = React.forwardRef<
    React.ElementRef<typeof CheckboxPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> &
        VariantProps<typeof checkboxVariants> & {
            indicatorClassName?: string;
        }
>(
    (
        {
            className,
            indicatorClassName,
            color,
            shape,
            groupHover,
            backgroundColor,
            ...props
        },
        ref,
    ) => (
        <CheckboxPrimitive.Root
            ref={ref}
            data-slot="checkbox"
            className={cn(
                checkboxVariants({
                    color,
                    shape,
                    groupHover,
                    backgroundColor,
                    className,
                }),
            )}
            {...props}
        >
            <CheckboxPrimitive.Indicator
                data-slot="checkbox-indicator"
                className="flex items-center justify-center text-current"
            >
                {shape !== 'round' && props.checked !== 'indeterminate' && (
                    <CheckIcon className={cn('size-3.5', indicatorClassName)} />
                )}
                {shape !== 'round' && props.checked === 'indeterminate' && (
                    <MinusIcon className={cn('size-3.5', indicatorClassName)} />
                )}
            </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
    ),
);

Checkbox.displayName = CheckboxPrimitive.Root.displayName;
